import { useState, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Play, ArrowDown } from 'lucide-react';
import { HERO_IMAGE, SIGNATURE_FILM } from '@/lib/data';
import { VideoModal } from './VideoModal';

const LINE_1 = 'Love,';
const LINE_2 = 'directed.';

const containerV = {
    hidden: {},
    show: { transition: { staggerChildren: 0.09, delayChildren: 0.3 } },
};
const letterV = {
    hidden: { y: '110%' },
    show: { y: '0%', transition: { duration: 1.1, ease: [0.22, 1, 0.36, 1] } },
};

export const Hero = () => {
    const [playing, setPlaying] = useState(false);
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ['start start', 'end start'],
    });
    // Parallax: background drifts slower + scales, foreground stays
    const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '28%']);
    const bgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.22]);
    const dim = useTransform(scrollYProgress, [0, 0.8], [0.55, 0.9]);
    const copyY = useTransform(scrollYProgress, [0, 1], ['0%', '-18%']);
    const copyOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

    return (
        <>
            <section
                ref={ref}
                id="top"
                data-testid="hero-section"
                className="relative min-h-screen w-full overflow-hidden film-grain"
            >
                {/* Dark cinematic void with anamorphic lens flare */}
                <motion.div
                    style={{ y: bgY, scale: bgScale }}
                    className="absolute inset-0 z-0 will-change-transform bg-ink"
                >
                    {/* subtle vignette light */}
                    <div
                        className="absolute inset-0"
                        style={{
                            background:
                                'radial-gradient(ellipse 90% 60% at 55% 55%, rgba(60,80,120,0.18), transparent 60%)',
                        }}
                    />

                    {/* ANAMORPHIC LENS FLARE — horizontal streak with bloom */}
                    <div className="absolute left-0 right-0 top-[48%] -translate-y-1/2 pointer-events-none">
                        {/* outer wide bloom */}
                        <div
                            className="h-[32vh] w-full blur-3xl opacity-70"
                            style={{
                                background:
                                    'linear-gradient(90deg, transparent 0%, rgba(120,170,255,0.0) 15%, rgba(140,190,255,0.55) 50%, rgba(120,170,255,0.0) 85%, transparent 100%)',
                            }}
                        />
                    </div>
                    <div className="absolute left-0 right-0 top-[48%] -translate-y-1/2 pointer-events-none">
                        {/* mid streak — sharper */}
                        <div
                            className="h-[6vh] w-full blur-xl opacity-90"
                            style={{
                                background:
                                    'linear-gradient(90deg, transparent 0%, rgba(170,210,255,0.9) 48%, rgba(220,240,255,1) 52%, transparent 100%)',
                            }}
                        />
                    </div>
                    <div className="absolute left-0 right-0 top-[48%] -translate-y-1/2 pointer-events-none">
                        {/* core hairline */}
                        <div
                            className="h-[1.5px] w-full opacity-95"
                            style={{
                                background:
                                    'linear-gradient(90deg, transparent 0%, rgba(220,240,255,0) 10%, rgba(240,250,255,1) 50%, rgba(220,240,255,0) 90%, transparent 100%)',
                                boxShadow: '0 0 24px 6px rgba(180,215,255,0.5)',
                            }}
                        />
                    </div>

                    {/* flare "star point" center */}
                    <div className="absolute left-1/2 top-[48%] -translate-x-1/2 -translate-y-1/2 pointer-events-none">
                        <div
                            className="w-56 h-56 rounded-full blur-2xl opacity-80"
                            style={{
                                background:
                                    'radial-gradient(circle, rgba(230,245,255,0.9) 0%, rgba(140,190,255,0.3) 40%, transparent 70%)',
                            }}
                        />
                    </div>

                    {/* secondary ghost flare — upper right */}
                    <div
                        className="absolute w-44 h-44 rounded-full blur-3xl opacity-30 pointer-events-none"
                        style={{
                            top: '22%',
                            right: '18%',
                            background:
                                'radial-gradient(circle, rgba(229,192,123,0.5), transparent 70%)',
                        }}
                    />

                    {/* vertical anamorphic streak kiss — faint */}
                    <div className="absolute left-1/2 top-0 bottom-0 w-px pointer-events-none">
                        <div
                            className="w-full h-full opacity-10"
                            style={{
                                background:
                                    'linear-gradient(180deg, transparent 0%, rgba(200,220,255,0.6) 48%, rgba(200,220,255,0.6) 52%, transparent 100%)',
                            }}
                        />
                    </div>
                </motion.div>

                {/* Film overlays — kept light since bg is already dark */}
                <motion.div
                    style={{ opacity: dim }}
                    className="absolute inset-0 z-10 bg-gradient-to-b from-ink/30 via-transparent to-ink pointer-events-none"
                />
                <div className="absolute inset-0 z-10 bg-gradient-to-r from-ink/50 via-transparent to-ink/40 pointer-events-none" />

                {/* Vertical rail */}
                <div className="hidden lg:flex absolute left-6 top-1/2 -translate-y-1/2 z-20 vertical-rl text-[10px] uppercase tracking-[0.5em] text-bone-dim items-center gap-4">
                    <span className="w-px h-12 bg-bone-dim/40" />
                    <span>Reel 2026 · Signature Film</span>
                    <span className="w-px h-12 bg-bone-dim/40" />
                </div>

                {/* Framing marks (cinematic corners) */}
                <div className="pointer-events-none absolute inset-0 z-20">
                    <Corner className="top-20 left-6 md:top-24 md:left-10" />
                    <Corner className="top-20 right-6 md:top-24 md:right-10 rotate-90" />
                    <Corner className="bottom-10 left-6 md:left-10 -rotate-90" />
                    <Corner className="bottom-10 right-6 md:right-10 rotate-180" />
                </div>

                {/* Content */}
                <motion.div
                    style={{ y: copyY, opacity: copyOpacity }}
                    className="relative z-20 min-h-screen flex flex-col justify-end px-6 md:px-12 lg:px-20 pb-20 lg:pb-28"
                >
                    <div className="max-w-6xl">
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, delay: 0.15 }}
                            className="text-[11px] uppercase tracking-[0.45em] text-gold mb-7"
                            data-testid="hero-eyebrow"
                        >
                            A Bros On Board Production · Est. 2018
                        </motion.p>

                        {/* Masked line-by-line reveal */}
                        <motion.h1
                            variants={containerV}
                            initial="hidden"
                            animate="show"
                            data-testid="hero-title"
                            className="font-serif font-light text-bone leading-[0.9] tracking-tighter text-7xl sm:text-8xl lg:text-[10rem]"
                            aria-label="Love, directed."
                        >
                            <span aria-hidden className="block overflow-hidden pb-2">
                                <motion.span variants={letterV} className="inline-block">
                                    {LINE_1}
                                </motion.span>
                            </span>
                            <span aria-hidden className="block overflow-hidden pb-2">
                                <motion.span
                                    variants={letterV}
                                    className="inline-block italic text-gold"
                                >
                                    {LINE_2}
                                </motion.span>
                            </span>
                        </motion.h1>

                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, delay: 1.2 }}
                            className="mt-8 max-w-xl text-base sm:text-lg text-bone-dim font-light leading-relaxed"
                            data-testid="hero-subtitle"
                        >
                            We shoot weddings the way Netflix shoots movies — story first, frame
                            last. Cinematic films, editorial photography, brand cinema.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 1, delay: 1.5 }}
                            className="mt-12 flex flex-wrap items-center gap-6"
                        >
                            <button
                                onClick={() => setPlaying(true)}
                                data-testid="hero-cta-watch"
                                className="group inline-flex items-center gap-4 bg-bone text-ink pl-5 pr-7 py-4 text-xs uppercase tracking-[0.25em] hover:bg-gold transition-colors duration-300"
                            >
                                <span className="w-8 h-8 rounded-full bg-ink text-bone group-hover:bg-ink/90 flex items-center justify-center transition-colors">
                                    <Play size={11} className="fill-bone ml-0.5" />
                                </span>
                                Watch the Signature Film
                            </button>
                            <a
                                href="#contact"
                                data-testid="hero-cta-contact"
                                className="group inline-flex items-center gap-3 text-bone text-xs uppercase tracking-[0.25em] border-b border-white/30 pb-1 hover:border-gold hover:text-gold transition-all duration-300"
                            >
                                Cast your love story
                                <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
                            </a>
                        </motion.div>
                    </div>

                    {/* Meta strip */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 1.8 }}
                        className="mt-20 flex flex-wrap items-end justify-between gap-8 border-t border-white/10 pt-7"
                    >
                        <div className="flex items-center gap-3 text-bone-dim text-[10px] uppercase tracking-[0.4em]">
                            <ArrowDown size={14} className="animate-bounce" />
                            Scroll
                        </div>
                        <div className="flex flex-wrap gap-10 text-[10px] uppercase tracking-[0.3em] text-bone-dim">
                            <Stat n="200+" l="Weddings Filmed" />
                            <Stat n="14" l="Cities · 3 Countries" />
                            <Stat n="8 yrs" l="Directing Love Stories" />
                        </div>
                    </motion.div>
                </motion.div>
            </section>

            <VideoModal
                open={playing}
                onClose={() => setPlaying(false)}
                src={SIGNATURE_FILM.videoUrl}
                poster={SIGNATURE_FILM.poster}
                title={SIGNATURE_FILM.title}
                subtitle={SIGNATURE_FILM.subtitle}
            />
        </>
    );
};

const Stat = ({ n, l }) => (
    <div>
        <div className="text-bone text-base font-serif tracking-tight">{n}</div>
        <div className="mt-1">{l}</div>
    </div>
);

const Corner = ({ className }) => (
    <div className={`absolute w-5 h-5 ${className}`}>
        <span className="absolute top-0 left-0 w-full h-px bg-bone/30" />
        <span className="absolute top-0 left-0 h-full w-px bg-bone/30" />
    </div>
);
